/**
 * Accessibility Utilities
 * 
 * Shared helpers for WCAG compliance throughout the application.
 * These utilities handle common accessibility patterns like screen reader
 * announcements without cluttering component code.
 */

/**
 * Announces a message to screen readers using an ARIA live region.
 * 
 * Creates a temporary live region element that screen readers will read aloud,
 * then clears it after the announcement. This is useful for dynamic content
 * changes that need to be communicated to assistive technology users.
 * 
 * @param message - The text to announce to screen readers
 * @param politeness - 'polite' waits for user idle, 'assertive' interrupts immediately
 * 
 * @example
 * // Announce a selection change
 * announceToScreenReader('English selected');
 * 
 * // Announce an urgent message immediately
 * announceToScreenReader('Error: Form submission failed', 'assertive');
 */
export function announceToScreenReader(
    message: string,
    politeness: 'polite' | 'assertive' = 'polite'
): void {
    // Find or create the announcer element
    const announcerId = 'sr-announcer';
    let announcer = document.getElementById(announcerId);
    
    if (!announcer) {
        announcer = document.createElement('div');
        announcer.id = announcerId;
        announcer.setAttribute('role', 'status');
        announcer.setAttribute('aria-live', politeness);
        announcer.setAttribute('aria-atomic', 'true');
        // Visually hidden but available to screen readers
        announcer.className = 'sr-only';
        document.body.appendChild(announcer);
    }
    
    // Update politeness level if needed
    announcer.setAttribute('aria-live', politeness);
    
    // Set the message (screen reader will announce it)
    announcer.textContent = message;
    
    // Clear after a short delay so repeated announcements work
    setTimeout(() => {
        if (announcer) {
            announcer.textContent = '';
        }
    }, 1000);
}

/**
 * Handles keyboard events for clickable elements that aren't natively interactive.
 * 
 * Makes div/span elements with onClick handlers accessible by responding to
 * Enter and Space keys, matching native button behavior.
 * 
 * @param callback - The function to call when Enter or Space is pressed
 * @returns A keyboard event handler
 * 
 * @example
 * <div
 *   onClick={handleClick}
 *   onKeyDown={handleKeyboardClick(handleClick)}
 *   tabIndex={0}
 *   role="button"
 * >
 *   Click me
 * </div>
 */
export function handleKeyboardClick(
    callback: () => void
): (event: React.KeyboardEvent) => void {
    return (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };
}
